import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useNightlyForAllRooms} from 'components/Accommodation/hooks/useNightlyForAllRooms'
import {PrimaryRoomOfferTag} from 'components/Accommodation/Offers/PrimaryRoomOffer/PrimaryRoomOfferTag'
import {priceSuffixMessages} from 'components/Accommodation/Rooms/RoomOfferPrice/constants'
import {useIsraeliVatEligibility} from 'hooks/useIsraeliVatEligibility'
import {useVioTag} from 'hooks/useVioTag'
import {getPriceValue} from 'modules/accommodation/utils'
import {getAllTaxesIncluded, getCurrencyCode} from 'modules/meta/selectors'
import {
  getAnchorPriceNightlyPerRoom,
  getNumberOfRooms,
  getOfferAccessFlowSettings,
  getShowTotalPrices,
  getSplitBookingPriceSummary
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'

import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {hasAnchorPrice} from '@vio/core/src/offer/business/price'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {getRoundingStrategy} from '@vio/core/src/utils/number'
import {PriceComposition} from '@vio/shared/src/search/PriceComposition'

import {SelectedElement} from '../../../../utils/selectedElement'
import {useAccommodationTopOffers} from '../useAccommodationTopOffers'

export const HotelOfferCardPriceComposition = ({
  hotelId,
  offer,
  size,
  isSplitBooking = false
}: {
  hotelId: string
  offer: SearchOffer
  size: 'lg' | 'xl'
  isSplitBooking?: boolean
}) => {
  const showTotalPrices = useSelector(getShowTotalPrices)
  const {offerEntity} = useAccommodationTopOffers({hotelId})

  const {isLocked, isSpecial} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer.id)
  )

  const anchorPriceNightly = useSelector(state =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)
  const sbDetails = useSelector(state =>
    getSplitBookingPriceSummary(state, hotelId)
  )
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const nightlyForAllRooms = useNightlyForAllRooms(hotelId)

  const offerPrice = getPriceValue(
    offer,
    showTotalPrices,
    sbDetails,
    isSplitBooking
  )

  const price =
    showTotalPrices || isSplitBooking
      ? offerPrice
      : nightlyForAllRooms(offerPrice)

  const anchorPrice = isSplitBooking
    ? showTotalPrices
      ? anchorPriceTotal
      : anchorPriceNightly
    : showTotalPrices
      ? (anchorPriceTotal ?? 0) * numberOfRooms
      : anchorPriceNightly * numberOfRooms

  const includeTaxes = useSelector(getAllTaxesIncluded)
  const currencyCode = useSelector(getCurrencyCode)
  const {brandName} = useBrand()

  // "Only on Vio" exclusive offers show a purple price plus the deal tag,
  // matching the room offers.
  const vioTagDetails = useVioTag({
    price: price,
    anchorPrice: anchorPrice,
    currencyCode,
    offerTags: offer.tags
  })
  const {isVioTagVisible} = vioTagDetails

  const vioTag = (
    <PrimaryRoomOfferTag
      brandName={brandName}
      isSelected={false}
      vioTagDetails={vioTagDetails}
    />
  )
  const {hasAdditionalCharges} = useIsraeliVatEligibility(hotelId)
  const {formatMessage} = useIntl()

  const taxMessage = (
    <Box>
      <Text variant="bodyS">
        {formatMessage(
          hasAdditionalCharges
            ? priceSuffixMessages.additionalCharges
            : includeTaxes || showTotalPrices
              ? priceSuffixMessages.withTaxesAndFees
              : priceSuffixMessages.includingTaxes
        )}
      </Text>
    </Box>
  )

  const roundingStrategy = getRoundingStrategy()
  const shouldShowAnchorPrice = anchorPrice
    ? hasAnchorPrice(price, anchorPrice)
    : false

  return (
    <Box display="flex" flexDirection="column">
      {isVioTagVisible && <Box paddingBottom="s200">{vioTag}</Box>}
      <Box display="flex" flexDirection="row" justifyContent="flex-start">
        <PriceComposition
          className="--row-left-aligned"
          dataId={SelectedElement.Price}
          price={price}
          anchorPrice={anchorPrice}
          currencyCode={currencyCode}
          roundingStrategy={roundingStrategy}
          showAnchorPrice={shouldShowAnchorPrice}
          showTotal={showTotalPrices}
          showIndication
          indicationColor={cssTheme.colors.content.neutral.primary}
          indicationSize="bodyS"
          size={size}
          isAppLockedDeal={isLocked}
          isPrivateDeal={isSpecial || isVioTagVisible}
        />
      </Box>
      {taxMessage}
    </Box>
  )
}
